import React from "react";
import { Mail, Phone, ShieldCheck } from "lucide-react";

/**
 * Donateur reconnu SANS code (décision d'Avi, 30/09/2026) : « ✓ On vous
 * connaît déjà », et JAMAIS son nom ni aucune donnée de sa fiche — le serveur
 * ne les envoie d'ailleurs plus avant le code (recognize-donor).
 *
 * Bouton facultatif « Retrouver mes informations » : après la vérification
 * (lien reçu par email ou code reçu par SMS, selon ce que la synagogue
 * propose), la fiche revient pré-remplie et modifiable. Sans vérification,
 * le donateur remplit le formulaire et peut payer tout de suite.
 *
 * Partagé par les pages campagne, don et fête.
 *
 * Props :
 *  - methode        : "email" | "phone" — comment il a été reconnu
 *  - onRemplir      : ouvre la vérification (absent → pas de bouton)
 *  - onRecommencer  : autre personne / autre contact (absent → pas de lien)
 *  - aide           : petite ligne sous le bouton (facultatif)
 *  - rattachement   : dire que le don (ou la réservation) rejoint le dossier
 *                     existant et que le reçu fiscal portera le nom qui y
 *                     figure — le nom tapé sans vérification n'y est jamais
 *                     écrit. "don" | "reservation" | absent (rien)
 *  - textes         : libellés traduits (page de don bilingue)
 *  - children       : la vérification, affichée SOUS la carte, pleine
 *                     largeur (lisible sur téléphone) une fois ouverte
 */
const TEXTES = {
  titre: "On vous connaît déjà",
  reconnuEmail: "Reconnu par votre email",
  reconnuTelephone: "Reconnu par votre téléphone",
  // Pas de « code par email » : par email, c'est un LIEN à cliquer, et le
  // SMS n'existe pas dans toutes les synagogues (relecture du 01/10/2026).
  remplir: "Retrouver mes informations",
  recommencer: "Recommencer",
  rattachementDon: "Votre don sera ajouté à votre dossier : le reçu fiscal sera établi au nom qui y figure. Pour le vérifier ou le corriger, utilisez « Retrouver mes informations ».",
  rattachementReservation: "Votre réservation sera ajoutée à votre dossier, au nom qui y figure.",
};

export default function OnVousConnait({
  methode = "email",
  onRemplir,
  onRecommencer,
  aide,
  rattachement,
  textes,
  children,
}) {
  const t = { ...TEXTES, ...(textes || {}) };
  return (
    <div className="mb-3" data-testid="on-vous-connait">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5">
        <p className="text-sm font-semibold text-emerald-800">✓ {t.titre}</p>
        <div className="mt-1 flex items-center justify-between gap-2">
          <p className="inline-flex items-center gap-1 text-xs text-emerald-700">
            {methode === "phone"
              ? <><Phone className="h-3 w-3" aria-hidden="true" /> {t.reconnuTelephone}</>
              : <><Mail className="h-3 w-3" aria-hidden="true" /> {t.reconnuEmail}</>}
          </p>
          {onRecommencer && (
            <button type="button" onClick={onRecommencer}
              className="trm-cible-tactile shrink-0 text-xs text-emerald-700 underline hover:text-emerald-900">
              {t.recommencer}
            </button>
          )}
        </div>
        {onRemplir && (
          <button type="button" onClick={onRemplir} data-testid="remplir-mes-informations"
            className="mt-2.5 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-emerald-300 bg-white px-3 py-2.5 text-sm font-medium text-emerald-800 transition-colors hover:bg-emerald-100">
            <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t.remplir}
          </button>
        )}
        {rattachement && (
          <p className="mt-2 text-xs leading-relaxed text-emerald-800" data-testid="rattachement-dossier">
            {rattachement === "reservation" ? t.rattachementReservation : t.rattachementDon}
          </p>
        )}
        {aide && <p className="mt-1.5 text-[11px] leading-relaxed text-emerald-700/80">{aide}</p>}
      </div>
      {children && <div className="mt-2">{children}</div>}
    </div>
  );
}
